import React from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import type {
  PredictedLineup as PredictedLineupData,
  PredictedLineupPlayer,
  PredictedLineupSide,
  TeamRef,
} from '@/lib/data/provider';
import { localizeDigits } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeTeamName } from '@/lib/i18n/footballNames';

/** Position groups, top→bottom of the column, with their localized labels. */
const POSITION_ORDER: { key: PredictedLineupPlayer['position']; labelKey: string }[] = [
  { key: 'G', labelKey: 'match.posGoalkeeper' },
  { key: 'D', labelKey: 'match.posDefence' },
  { key: 'M', labelKey: 'match.posMidfield' },
  { key: 'F', labelKey: 'match.posAttack' },
];

/** Bucket a side's XI into the four position groups (preserving input order). */
function groupByPosition(
  players: PredictedLineupPlayer[],
): Record<PredictedLineupPlayer['position'], PredictedLineupPlayer[]> {
  const groups: Record<PredictedLineupPlayer['position'], PredictedLineupPlayer[]> = {
    G: [],
    D: [],
    M: [],
    F: [],
  };
  for (const player of players) {
    (groups[player.position] ?? groups.F).push(player);
  }
  return groups;
}

/** One player row: jersey number then the short name (falling back to full name). */
function PlayerRow({ player, align }: { player: PredictedLineupPlayer; align: 'start' | 'end' }) {
  const palette = useThemePalette();
  const { arabicNumerals } = useLocale();
  const name = player.short_name || player.name;
  const justify = align === 'end' ? 'justify-end' : 'justify-start';
  const jersey = (
    <View
      key="jersey"
      className="min-w-[22px] items-center rounded-md px-1 py-0.5"
      style={{ backgroundColor: palette.inset }}
    >
      <AppText variant="micro" weight="bold" tabular style={{ color: palette.voltBright }}>
        {localizeDigits(String(player.jersey_number), arabicNumerals)}
      </AppText>
    </View>
  );
  const label = (
    <AppText
      key="name"
      variant="caption"
      weight="medium"
      className={`flex-1 text-ink ${align === 'end' ? 'text-right' : ''}`}
      numberOfLines={1}
    >
      {name}
    </AppText>
  );
  return (
    <View className={`flex-row items-center gap-2 ${justify}`}>
      {align === 'end' ? [label, jersey] : [jersey, label]}
    </View>
  );
}

/** One team column: crest + name + formation header, then the grouped XI. */
function TeamColumn({
  side,
  team,
  align,
}: {
  side: PredictedLineupSide | null | undefined;
  team: TeamRef | null;
  align: 'start' | 'end';
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  // The stored predicted_lineup blob is unvalidated — a side (or its players)
  // may be missing even when the sibling side exists.
  const groups = groupByPosition(side?.players ?? []);
  const itemsAlign = align === 'end' ? 'items-end' : 'items-start';

  return (
    <View className="flex-1 gap-3">
      <View className={`gap-1.5 ${itemsAlign}`}>
        <TeamCrest uri={team?.logo_url} name={localizeTeamName(team, locale)} size="sm" />
        <AppText
          variant="caption"
          weight="bold"
          className={`text-ink ${align === 'end' ? 'text-right' : ''}`}
          numberOfLines={1}
        >
          {localizeTeamName(team, locale, true)}
        </AppText>
        {side?.formation ? (
          <AppText variant="micro" weight="medium" tabular className="text-soft">
            {side.formation}
          </AppText>
        ) : null}
      </View>

      <View className="gap-2.5">
        {POSITION_ORDER.map(({ key, labelKey }) => {
          const lines = groups[key];
          if (lines.length === 0) return null;
          return (
            <View key={key} className="gap-1.5">
              <AppText
                variant="micro"
                weight="bold"
                className={`uppercase tracking-wide text-muted ${align === 'end' ? 'text-right' : ''}`}
              >
                {t(labelKey)}
              </AppText>
              {lines.map((player, index) => (
                <PlayerRow key={`${player.id}-${index}`} player={player} align={align} />
              ))}
            </View>
          );
        })}
      </View>
    </View>
  );
}

/**
 * The pre-match predicted XI for both sides: two team columns (home on the left,
 * away on the right — the row mirrors naturally in RTL) each showing the crest,
 * name, formation and the projected eleven grouped by position (GK / Def / Mid /
 * Att). A small "Predicted" badge flags that this is a forecast, not a confirmed
 * sheet. The caller hides the block when `lineup` is null; this also self-hides
 * if BOTH sides come back with no players.
 */
export function PredictedLineup({
  lineup,
  homeTeam,
  awayTeam,
}: {
  lineup: PredictedLineupData;
  homeTeam: TeamRef | null;
  awayTeam: TeamRef | null;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();

  const homePlayers = lineup.home?.players ?? [];
  const awayPlayers = lineup.away?.players ?? [];
  if (homePlayers.length === 0 && awayPlayers.length === 0) return null;

  const isPredicted = lineup.lineup_status === 'predicted';

  return (
    <Card className="overflow-hidden">
      <View className="flex-row items-center justify-between px-4 pb-1 pt-3">
        <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
          {t('match.predictedLineup')}
        </AppText>
        {isPredicted ? (
          <View
            className="rounded-full border px-2 py-0.5"
            style={{ backgroundColor: palette.voltGlass, borderColor: palette.voltLine }}
          >
            <AppText variant="micro" weight="bold" style={{ color: palette.voltBright }}>
              {t('match.predictedBadge')}
            </AppText>
          </View>
        ) : null}
      </View>

      <View className="flex-row gap-3 px-4 pb-4 pt-2">
        <TeamColumn side={lineup.home} team={homeTeam} align="start" />
        <View style={{ width: 1, backgroundColor: palette.line }} />
        <TeamColumn side={lineup.away} team={awayTeam} align="end" />
      </View>
    </Card>
  );
}
